import { useContext, useEffect } from 'react';

import { Money, formatMoney } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, Input, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { MAXIMUM_GOAL_TERM_YEARS } from '../../../../../GoalForecaster/constants';
import { convertToMoneyWithDefault, hasMaskedFormFieldChanged } from '../../../../shared/helpers/utils';
import { ForecasterPurposeInputsContext, ProjectionInputs } from '../../../context/ForecasterPurposeInputsContext';
import { useRetirementGrowthProjection } from '../../../context/ForecasterPurposeProjectionContext';
import { RetirementGrowthSettingsPurpose } from '../../../data';
import { handleSaveRetirementTarget, handleSetupAnnualContributions } from '../utils';

type Props = {
  purpose: RetirementGrowthSettingsPurpose;
};

const schema = (currentAge: number) =>
  Yup.object().shape({
    annualRecurringDeposits: Yup.string().optional(),
    targetMonthlySpendingAmount: Yup.string().optional(),
    retirementAge: Yup.number()
      .required('Retirement age is required')
      .min(currentAge + 1, 'Must be greater than your current age')
      .max(currentAge + MAXIMUM_GOAL_TERM_YEARS, `Must be within the next ${MAXIMUM_GOAL_TERM_YEARS} years`),
    retirementZip: Yup.string()
      .required('Retirement zip code is required')
      .matches(/^[0-9]{5}$/, 'Must be 5 digits'),
  });

type FormikFormProps = {
  recommendedAnnualInflows?: Money | null;
  updatedFormInputs?: ProjectionInputs['retirementGrowth'];
  goalId: number;
};

const FormikForm = ({ recommendedAnnualInflows, updatedFormInputs, goalId }: FormikFormProps) => {
  const { handleSubmit, initialValues, setFieldValue, submitForm, validateForm, values } = useFormikContext<{
    annualRecurringDeposits?: string;
    targetMonthlySpendingAmount?: string;
    retirementAge?: number;
    retirementZip?: string;
  }>();

  useEffect(() => {
    void validateForm();
    void submitForm();
  }, []);

  const handleFormChange = async () => {
    await validateForm();
    handleSubmit();
  };

  const shouldShowRecommendedAnnualInflows =
    recommendedAnnualInflows && recommendedAnnualInflows?.cents !== updatedFormInputs?.annualRecurringDeposits?.cents;

  const handleRecommendedAnnualInflowsClick = async () => {
    if (recommendedAnnualInflows) {
      await setFieldValue('annualRecurringDeposits', formatMoney(recommendedAnnualInflows, 'raw'), true);
      await handleFormChange();
    }
  };

  return (
    <Form>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.300' }}>
        <Box>
          <Field name="annualRecurringDeposits">
            <Field.Label>Planned annual contributions</Field.Label>
            <Field.Control>
              <Input mask="number" leadingSymbol="$" placeholder="0" onChange={handleFormChange} />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          {shouldShowRecommendedAnnualInflows && (
            <Text variant="text50" css={{ mt: 'space.50' }}>
              Recommended:{' '}
              <Button variant="textPrimary" onClick={handleRecommendedAnnualInflowsClick}>
                {`${formatMoney(recommendedAnnualInflows)}/year`}
              </Button>
            </Text>
          )}
        </Box>
        {values.annualRecurringDeposits &&
          hasMaskedFormFieldChanged(values.annualRecurringDeposits, initialValues.annualRecurringDeposits) && (
            <SambaAutoLink to={handleSetupAnnualContributions(values.annualRecurringDeposits, goalId)}>
              Save
            </SambaAutoLink>
          )}
        <Separator />

        <Field name="targetMonthlySpendingAmount">
          <Field.Label>Desired monthly spending power</Field.Label>
          <Field.Control>
            <Input mask="number" leadingSymbol="$" placeholder="0" onChange={handleFormChange} />
          </Field.Control>
          <Field.Description>
            <Text variant="text50" color="text.weak">
              In today's dollars
            </Text>
          </Field.Description>
          <Field.ErrorMessage />
        </Field>
        <Box css={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'space.100' }}>
          <Field name="retirementAge">
            <Field.Label>Retirement age</Field.Label>
            <Field.Control>
              <Input type="number" placeholder="65" min={0} max={200} onChange={handleFormChange} />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
          <Field name="retirementZip">
            <Field.Label>Retirement zip code</Field.Label>
            <Field.Control>
              <Input type="text" min={5} maxLength={5} onChange={handleFormChange} />
            </Field.Control>
            <Field.ErrorMessage />
          </Field>
        </Box>
        {values.targetMonthlySpendingAmount &&
          values.retirementAge &&
          values.retirementZip &&
          (hasMaskedFormFieldChanged(values.targetMonthlySpendingAmount, initialValues.targetMonthlySpendingAmount) ||
            hasMaskedFormFieldChanged(values.retirementAge.toString(), initialValues.retirementAge?.toString()) ||
            hasMaskedFormFieldChanged(values.retirementZip, initialValues.retirementZip)) && (
            <SambaAutoLink
              to={handleSaveRetirementTarget({
                goalId,
                retirementAge: values.retirementAge,
                retirementZip: values.retirementZip,
                targetMonthlySpendingAmount: values.targetMonthlySpendingAmount,
              })}
            >
              Save
            </SambaAutoLink>
          )}
      </Box>
    </Form>
  );
};

export const RetirementGrowthForm: React.FC<Props> = ({ purpose }) => {
  const inputsContext = useContext(ForecasterPurposeInputsContext);
  const { purpose: projectionPurpose } = useRetirementGrowthProjection();
  if (!inputsContext) return null;

  const {
    projectionQueryInputs: { retirementGrowth: updatedFormInputs },
    update: { retirementGrowthProjectionInput: updateInputs },
  } = inputsContext;

  const onFormSubmit = (values: {
    annualRecurringDeposits?: string;
    targetMonthlySpendingAmount?: string;
    retirementAge?: number;
    retirementZip?: string;
  }) => {
    if (!values.retirementAge || !values.retirementZip) {
      throw new Error('Both retirement age and zip code are required');
    }

    updateInputs({
      annualRecurringDeposits: convertToMoneyWithDefault(values.annualRecurringDeposits),
      targetMonthlySpendingAmount: convertToMoneyWithDefault(values.targetMonthlySpendingAmount),
      retirementAge: Number(values.retirementAge),
      retirementZip: values.retirementZip,
    });
  };

  const validationSchema = schema(purpose.financialProfile.retirementAssumptions?.userAssumptions.currentAge || 0);

  return (
    <Formik
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
      initialValues={{
        annualRecurringDeposits: !purpose.envelope.totalAnnualRecurringInflows?.cents
          ? ''
          : formatMoney(purpose.envelope.totalAnnualRecurringInflows, 'raw'),
        targetMonthlySpendingAmount: !purpose.targetMonthlySpendingAmount?.cents
          ? ''
          : formatMoney(purpose.targetMonthlySpendingAmount, 'raw'),
        retirementAge: purpose.financialProfile.retirementAssumptions?.userAssumptions.retirementAge,
        retirementZip: purpose.financialProfile.retirementAssumptions?.retirementZip,
      }}
    >
      <FormikForm
        recommendedAnnualInflows={
          projectionPurpose?.retirementGrowthProjection?.completion?.recommendedTotalAnnualContributions
        }
        updatedFormInputs={updatedFormInputs}
        goalId={purpose.goalId}
      />
    </Formik>
  );
};
